import React from 'react';

// ============================================
// BUTTON COMPONENT
// ============================================
// A flexible button component with multiple variants, sizes, and states.
// Uses the design system tokens from tailwind.config.js

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  children?: React.ReactNode;
}

// Variant styles - background, text, border, hover, focus
const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-primary-600 text-white border-transparent
    hover:bg-primary-700
    focus-visible:ring-neutral-400
    disabled:bg-primary-300
  `,
  secondary: `
    bg-neutral-100 text-neutral-700 border-transparent
    hover:bg-neutral-200
    focus-visible:ring-neutral-400
    disabled:bg-neutral-100 disabled:text-neutral-400
  `,
  outline: `
    bg-transparent text-neutral-700 border-neutral-300
    hover:bg-neutral-50 hover:border-neutral-400
    focus-visible:ring-neutral-400
    disabled:text-neutral-400 disabled:border-neutral-200
  `,
  ghost: `
    bg-transparent text-neutral-600 border-transparent
    hover:bg-neutral-100 hover:text-neutral-900
    focus-visible:ring-neutral-400
    disabled:text-neutral-400
  `,
  danger: `
    bg-danger-600 text-white border-transparent
    hover:bg-danger-700
    focus-visible:ring-neutral-400
    disabled:bg-danger-300
  `,
  success: `
    bg-success-600 text-white border-transparent
    hover:bg-success-700
    focus-visible:ring-neutral-400
    disabled:bg-success-300
  `,
};

// Size styles - padding, font size, height
const sizeStyles: Record<ButtonSize, string> = {
  xs: 'px-2 py-1 text-xs h-7',
  sm: 'px-3 py-1.5 text-sm h-9',
  md: 'px-4 py-2 text-sm h-10',
  lg: 'px-5 py-2.5 text-base h-12',
};

// Icon size based on button size
const iconSizeStyles: Record<ButtonSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

// Base styles applied to all buttons
const baseStyles = `
  inline-flex items-center justify-center
  font-medium
  border rounded-lg
  transition-colors duration-fast
  focus:outline-none focus-visible:ring-1 focus-visible:ring-offset-1
  disabled:cursor-not-allowed disabled:opacity-60
`;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      children,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Combine all styles
    const buttonClasses = [
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      fullWidth ? 'w-full' : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Loading spinner
    const LoadingSpinner = () => (
      <svg
        className={`animate-spin ${iconSizeStyles[size]} ${children ? (iconPosition === 'left' ? 'mr-2' : 'ml-2') : ''}`}
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    // Icon element with proper spacing
    const IconElement = icon && !loading && (
      <span className={`${iconSizeStyles[size]} ${children ? (iconPosition === 'left' ? 'mr-2' : 'ml-2') : ''}`}>
        {icon}
      </span>
    );

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={isDisabled}
        {...props}
      >
        {loading && iconPosition === 'left' && <LoadingSpinner />}
        {!loading && iconPosition === 'left' && IconElement}
        {children}
        {!loading && iconPosition === 'right' && IconElement}
        {loading && iconPosition === 'right' && <LoadingSpinner />}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
